<template>
  <div class="asignacion-paquetes">
    <form-wizard
      color="#0080C9"
      layout="horizontal"
      :title="null"
      :subtitle="null"
      next-button-text="Siguiente"
      back-button-text="Atrás"
      finish-button-text="Finalizar"
      @on-complete="submitAsignacion"
      class="wizard-vertical"
    >
      <template #footer="{ activeTabIndex, isLastStep, nextTab, prevTab, fillButtonStyle }">
        <div class="wizard-footer d-flex justify-content-between flex-wrap mt-3">
          <b-button
            v-if="activeTabIndex > 0"
            variant="primary"
            class="wizard-button"
            :style="fillButtonStyle"
            @click="prevTab()"
          >
            Atrás
          </b-button>

          <div class="ml-auto">
            <b-button
              v-if="!isLastStep"
              variant="primary"
              class="wizard-button"
              :style="fillButtonStyle"
              @click="handleNext(activeTabIndex, nextTab)"
            >
              Siguiente
            </b-button>
            <b-button
              v-else
              variant="primary"
              class="wizard-button"
              :disabled="isSubmitting"
              :style="fillButtonStyle"
              @click="submitAsignacion"
            >
              {{ isSubmitting ? 'Guardando...' : 'Finalizar' }}
            </b-button>
          </div>
        </div>
      </template>

      <tab-content
        title="Información"
        :before-change="validatePasoInformacion"
      >
        <b-row>
          <b-col md="6">
            <b-form-group label="Paciente" label-class="font-weight-bold">
              <SearchOpts
                v-model="formulario.paciente"
                :search-service="buscarPaciente"
                placeholder="Buscar por nombre o cédula..."
                :min-chars="3"
              />
              <small v-if="errores.paciente" class="text-danger">{{ errores.paciente }}</small>
            </b-form-group>

            <b-form-group label="Correo" v-if="pacienteCorreo">
              <b-form-input :value="pacienteCorreo" disabled />
            </b-form-group>
            <b-form-group label="Teléfono" v-if="pacienteTelefono">
              <b-form-input :value="pacienteTelefono" disabled />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Paquete preventivo" label-class="font-weight-bold">
              <SearchOpts
                v-model="formulario.paquete"
                :search-service="buscarPaquete"
                placeholder="Buscar por código o nombre..."
                :min-chars="2"
              />
              <small v-if="errores.paquete" class="text-danger">{{ errores.paquete }}</small>
            </b-form-group>

            <b-card v-if="paqueteSeleccionado" class="shadow-sm border-primary">
              <h5 class="text-primary mb-2">{{ paqueteSeleccionado.nombre }}</h5>
              <p class="mb-1"><strong>Código:</strong> {{ paqueteSeleccionado.codigo }}</p>
              <p class="mb-1"><strong>Precio:</strong> {{ paqueteSeleccionado.precio | currency }}</p>
              <p class="mb-0" v-if="paqueteSeleccionado.descripcion">
                <strong>Descripción:</strong> {{ paqueteSeleccionado.descripcion }}
              </p>
            </b-card>
          </b-col>
        </b-row>
      </tab-content>

      <tab-content
        title="Servicios"
        :before-change="validatePasoServicios"
      >
        <div v-if="!paqueteSeleccionado" class="text-center py-5 text-muted">
          Selecciona primero un paquete en la pestaña Información.
        </div>
        <div v-else>
          <b-alert variant="info" show>
            Los servicios se precargan automáticamente desde el paquete seleccionado. Puedes editar los detalles antes de continuar.
          </b-alert>

          <b-alert
            v-if="!tiposServiciosFiltrados.length"
            variant="warning"
            show
          >
            El paquete seleccionado no tiene servicios configurados.
          </b-alert>

          <section
            v-for="tipo in tiposServiciosFiltrados"
            :key="tipo.key"
            class="mb-4"
          >
            <h5 class="text-primary mb-2">{{ tipo.title }}</h5>

            <b-card
              v-for="(grupo, index) in serviciosAgrupados[tipo.key]"
              :key="`${tipo.key}-${index}`"
              class="mb-2 servicio-card"
            >
              <b-row>
                <b-col :md="tipo.key === 'laboratorios' ? 12 : 9">
                  <template v-if="tipo.key === 'laboratorios'">
                    <p class="mb-1" v-if="grupo.numeroOrden"><strong>N° de orden:</strong> {{ grupo.numeroOrden }}</p>
                    <p class="mb-1" v-if="grupo.detalle"><strong>Detalle:</strong> {{ grupo.detalle }}</p>
                    <p class="mb-1"><strong>Laboratorios:</strong></p>
                    <ul class="lista-laboratorios mb-0">
                      <li v-for="(lab, labIndex) in grupo.items" :key="`${tipo.key}-${index}-${labIndex}`">
                        ({{ lab.item.codigo || 'N/D' }}) {{ lab.item.descripcion || lab.item.label }}
                      </li>
                    </ul>
                  </template>
                  <template v-else>
                    <p class="mb-1"><strong>Descripción:</strong> {{ grupo.items[0].item.descripcion || grupo.items[0].item.label }}</p>
                    <p class="mb-1" v-if="grupo.items[0].item.loteCantidad > 1">
                      <strong>Sesión:</strong> {{ `${grupo.items[0].item.loteIndex} de ${grupo.items[0].item.loteCantidad}` }}
                    </p>
                    <p class="mb-1" v-if="grupo.items[0].item.numeroOrden && grupo.items[0].item.tipo !== 'atenciones'"><strong>N° de orden:</strong> {{ grupo.items[0].item.numeroOrden }}</p>
                    <p class="mb-1" v-if="grupo.items[0].item.detalle"><strong>Detalle:</strong> {{ grupo.items[0].item.detalle }}</p>
                    <p class="mb-1" v-if="grupo.items[0].item.tipo === 'imagenes' && grupo.items[0].item.lateralidad"><strong>Lateralidad:</strong> {{ grupo.items[0].item.lateralidad }}</p>
                    <p class="mb-1" v-if="grupo.items[0].item.tipo === 'imagenes' && grupo.items[0].item.requiereSedacion"><strong>Requiere sedación:</strong> Sí</p>
                    <p
                      class="mb-1"
                      v-if="grupo.items[0].item.tipo === 'atenciones' && (grupo.items[0].item.modalidadDescripcion || grupo.items[0].item.tipoAtencion)"
                    >
                      <strong>Modalidad:</strong> {{ grupo.items[0].item.modalidadDescripcion || grupo.items[0].item.tipoAtencion }}
                    </p>
                    <p class="mb-1" v-if="grupo.items[0].item.tipo === 'atenciones' && obtenerNombreDoctor(grupo.items[0].item)"><strong>Doctor asignado:</strong> {{ obtenerNombreDoctor(grupo.items[0].item) }}</p>
                    <p class="mb-1" v-if="grupo.items[0].item.tipo === 'atenciones' && grupo.items[0].item.fechaCita"><strong>Fecha:</strong> {{ grupo.items[0].item.fechaCita }}</p>
                    <p class="mb-1" v-if="grupo.items[0].item.tipo === 'atenciones' && grupo.items[0].item.horaCita"><strong>Hora:</strong> {{ grupo.items[0].item.horaCita }}</p>
                  </template>
                </b-col>
                <b-col md="3" class="text-right" v-if="tipo.key !== 'laboratorios'">
                  <b-button size="sm" variant="warning" class="mr-1" @click="abrirModalEdicion(tipo.key, grupo.items[0].index)">
                    <feather-icon icon="EditIcon" class="mr-25" /> Editar
                  </b-button>
                </b-col>
              </b-row>
            </b-card>
          </section>
        </div>
      </tab-content>

      <tab-content
        title="Documentos"
        :before-change="validatePasoDocumentos"
      >
        <b-card class="p-3">
          <h5 class="mb-3">Documentos del paciente</h5>
          <LoadFileInput
            label="Otros documentos"
            v-model="documentos.otro"
            :url-file="documentosPreview.otro"
          />
        </b-card>
      </tab-content>

      <tab-content title="Encuesta" :before-change="validatePasoEncuesta">
        <b-card class="p-4 text-center">
          <h4 class="mb-3">Encuesta de satisfacción</h4>
          <p class="text-muted">Califique la experiencia del paciente (1 = muy insatisfecho, 4 = muy satisfecho).</p>

          <div class="d-flex justify-content-center align-items-center">
            <b-form-input
              v-model.number="formulario.encuesta"
              type="password"
              inputmode="numeric"
              maxlength="1"
              autocomplete="off"
              placeholder="•"
              @input="onEncuestaInput"
              class="encuesta-input"
              :disabled="modoEdicion"
            />
          </div>
          <small v-if="errores.encuesta" class="text-danger d-block mt-2">{{ errores.encuesta }}</small>

          <b-alert v-if="encuestaValida" variant="success" show class="mt-3">
            Gracias. Puedes finalizar la asignación cuando estés listo.
          </b-alert>
        </b-card>
      </tab-content>

      <tab-content title="Finalizar">
        <b-card class="p-4 resumen-final">
          <h4 class="mb-4">Resumen de la asignación</h4>

          <ResumenAsignacionPaquete
            :resumen-paciente="resumenPaciente"
            :resumen-paquete="resumenPaquete"
            :resumen-servicios="resumenServicios"
            :resumen-documentos="resumenDocumentos"
            @ver-documento="onVerDocumentoResumen"
          />
        </b-card>
      </tab-content>
    </form-wizard>

    <ModalViewFile
      v-if="visorDocumento.url"
      v-model="visorDocumento.visible"
      :url-file="visorDocumento.url"
      :ruta-completa="visorDocumento.rutaCompleta"
    />

    <b-modal
      id="modal-editar-servicio"
      :title="modalEdicion.titulo"
      ok-title="Guardar"
      cancel-title="Cancelar"
      @ok="guardarEdicionServicio"
      @hidden="resetModalEdicion"
      v-model="modalEdicion.visible"
      size="lg"
    >
      <template v-if="modalEdicion.form">
        <b-form @submit.stop.prevent>
          <b-row>
            <b-col cols="12">
              <b-form-group label="Descripción">
                <b-form-input v-model="modalEdicion.form.descripcion" disabled />
              </b-form-group>
            </b-col>

            <b-col md="6" v-if="modalEdicion.tipo === 'imagenes' || modalEdicion.tipo === 'procedimientos' || modalEdicion.tipo === 'laboratorios'">
              <b-form-group label="Número de orden">
                <b-form-input v-model="modalEdicion.form.numeroOrden" disabled />
                <small class="text-muted">Se genera automáticamente.</small>
              </b-form-group>
            </b-col>

            <b-col md="6" v-if="modalEdicion.tipo === 'imagenes'">
              <b-form-group label="Lateralidad">
                <b-form-select v-model="modalEdicion.form.lateralidad" :options="lateralidades" />
              </b-form-group>
            </b-col>

            <b-col md="6" v-if="modalEdicion.tipo === 'imagenes'">
              <b-form-group label="Requiere sedación?">
                <b-form-checkbox switch v-model="modalEdicion.form.requiereSedacion">
                  {{ modalEdicion.form.requiereSedacion ? 'Sí' : 'No' }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <template v-if="modalEdicion.tipo === 'procedimientos'">
              <b-col cols="12">
                <b-form-group label="Motivo de solicitud">
                  <b-form-textarea
                    v-model="modalEdicion.form.motivo"
                    rows="2"
                    placeholder="Escriba el motivo de la solicitud"
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <b-form-group label="Resumen clínico">
                  <b-form-textarea
                    v-model="modalEdicion.form.resumenClinico"
                    rows="2"
                    placeholder="Ingrese el resumen clínico"
                  />
                </b-form-group>
              </b-col>
            </template>

            <template
              v-else-if="modalEdicion.tipo !== 'imagenes' && modalEdicion.tipo !== 'atenciones' && modalEdicion.tipo !== 'laboratorios'"
            >
              <b-col cols="12">
                <b-form-group label="Detalle">
                  <b-form-textarea
                    v-model="modalEdicion.form.detalle"
                    rows="3"
                    placeholder="Agregar notas relevantes del servicio"
                  />
                </b-form-group>
              </b-col>
            </template>

            <b-col cols="12" v-if="modalEdicion.tipo === 'imagenes'">
              <b-form-group label="Motivo de solicitud">
                <b-form-textarea
                  v-model="modalEdicion.form.motivo"
                  rows="2"
                  placeholder="Escriba el motivo de la solicitud"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" v-if="modalEdicion.tipo === 'imagenes'">
              <b-form-group label="Resumen clínico">
                <b-form-textarea
                  v-model="modalEdicion.form.resumenClinico"
                  rows="2"
                  placeholder="Ingrese el resumen clínico"
                />
              </b-form-group>
            </b-col>

            <template v-if="modalEdicion.tipo === 'atenciones'">
              <b-col md="6">
                <b-form-group label="Tipo de atención médica">
                  <SearchOpts
                    v-model="modalEdicion.form.atencionSeleccion"
                    :search-service="buscarTipoAtencionMedica"
                    :min-chars="3"
                    placeholder="Buscar por código o descripción..."
                  />
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group label="Modalidad">
                  <b-form-select
                    v-model="modalEdicion.form.modalidadId"
                    :options="modalidades"
                    :disabled="!modalidades.length"
                    @change="onChangeModalidad"
                  />
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group label="Doctor">
                  <SearchOpts
                    v-model="modalEdicion.form.doctor"
                    :search-service="termino => buscarDoctorPorEspecialidad(termino, modalEdicion.form.especialidadId)"
                    :min-chars="2"
                    placeholder="Buscar doctor por nombre..."
                  />
                  <small v-if="!modalEdicion.form.especialidadId" class="text-muted">El paquete no tiene una especialidad asociada.</small>
                </b-form-group>
              </b-col>

              <b-col md="3">
                <b-form-group label="Fecha de la cita">
                  <flat-pickr
                    v-model="modalEdicion.form.fechaCita"
                    class="form-control"
                    :config="fechaCitaConfig"
                  />
                </b-form-group>
              </b-col>

              <b-col md="3">
                <b-form-group label="Hora de la cita">
                  <flat-pickr
                    v-model="modalEdicion.form.horaCita"
                    class="form-control"
                    :config="horaCitaConfig"
                  />
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group label="Tipo de contingencia">
                  <b-form-input v-model="modalEdicion.form.tipoContingencia" disabled />
                </b-form-group>
              </b-col>
            </template>
          </b-row>
        </b-form>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import flatPickr from 'vue-flatpickr-component'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import 'flatpickr/dist/flatpickr.css'

import {
  BButton,
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormCheckbox,
  BAlert,
  BModal,
} from 'bootstrap-vue'

import axios from 'axios'
import rutas from '@/assets/utils/rutas.json'
import { SearchOpts, LoadFileInput, ModalViewFile } from '@/shared/components'
import ResumenAsignacionPaquete from './ResumenAsignacionPaquete.vue'
import { pacientesService } from '@/views/admision/services'
import { doctoresService } from '@/views/administracion/services'
import { paquetesService } from '@/views/enfermedadesPreventivas/services/paquetesService'
import { carteraAtencionesService } from '@/views/atencionMedica/services/atencionMedicaService/carteraAtencionesService'
import { asignacionPaquetesService } from '@/views/enfermedadesPreventivas/services/asignacionPaquetesService'
import { getEstablecimientoLS } from '@/shared/helpers/dataLocalStorage'

const STORAGE_KEY = 'asignacion-paquete-seleccionado'

export default {
  name: 'AsignacionPaquetesModal',

  props: {
    initialData: {
      type: Object,
      default: () => null,
    },
  },

  emits: ['saved'],

  components: {
    FormWizard,
    TabContent,
    SearchOpts,
    LoadFileInput,
    ModalViewFile,
    BButton,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormCheckbox,
    BAlert,
    BModal,
    flatPickr,
    ResumenAsignacionPaquete,
  },

  data() {
    return {
      formulario: {
        paciente: null,
        paquete: null,
        encuesta: null,
      },
      errores: {
        paciente: null,
        paquete: null,
        encuesta: null,
      },
      paqueteSeleccionado: null,
      servicios: {
        imagenes: [],
        laboratorios: [],
        procedimientos: [],
        atenciones: [],
        terapias: [],
      },
      numeroOrdenLaboratorios: null,
      documentos: {
        otro: null,
      },
      documentosPreview: {
        otro: null,
      },
      visorDocumento: {
        visible: false,
        url: null,
        rutaCompleta: false,
      },
      modalEdicion: {
        visible: false,
        tipo: null,
        indice: null,
        titulo: '',
        form: null,
      },
      isSubmitting: false,
      modoEdicion: false,
      lateralidades: [
        { value: null, text: 'Seleccione una opción' },
        { value: 'Antero-posterior (AP)', text: 'Antero-posterior (AP)' },
        { value: 'Postero-anterior (PA)', text: 'Postero-anterior (PA)' },
        { value: 'Derecha', text: 'Derecha' },
        { value: 'Izquierda', text: 'Izquierda' },
        { value: 'Bilateral', text: 'Bilateral' },
        { value: 'Ninguno', text: 'Ninguno' },
      ],
      tiposServicios: [
        { key: 'imagenes', title: 'Imágenes' },
        { key: 'laboratorios', title: 'Laboratorios' },
        { key: 'procedimientos', title: 'Procedimientos' },
        { key: 'terapias', title: 'Terapias' },
        { key: 'atenciones', title: 'Atenciones médicas' },
      ],
      fechaCitaConfig: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d',
      },
      horaCitaConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
      },
      defaultTipoContingencia: 'ENFERMEDAD GENERAL',
      modalidades: [],
    }
  },

  computed: {
    pacienteSeleccionado() {
      return this.formulario.paciente || null
    },

    pacienteCorreo() {
      const persona = this.formulario.paciente && this.formulario.paciente.persona
      return persona && persona.correo ? persona.correo : ''
    },

    pacienteTelefono() {
      const persona = this.formulario.paciente && this.formulario.paciente.persona
      return persona && persona.telefono ? persona.telefono : ''
    },

    encuestaValida() {
      const valor = Number(this.formulario.encuesta)
      return Number.isInteger(valor) && valor >= 1 && valor <= 4
    },

    tiposServiciosFiltrados() {
      return this.tiposServicios.filter(item => {
        const lista = this.servicios[item.key]
        return Array.isArray(lista) && lista.length > 0
      })
    },

    serviciosAgrupados() {
      const resultado = {}

      this.tiposServicios.forEach(({ key }) => {
        const lista = Array.isArray(this.servicios[key]) ? this.servicios[key] : []

        if (key !== 'laboratorios') {
          resultado[key] = lista.map((item, index) => ({
            numeroOrden: item.numeroOrden || null,
            detalle: item.detalle || '',
            items: [{ item, index }],
          }))
          return
        }

        const grupos = []
        const mapa = new Map()

        lista.forEach((item, index) => {
          const numeroOrden = item.numeroOrden || 'SIN-ORDEN'

          if (!mapa.has(numeroOrden)) {
            mapa.set(numeroOrden, {
              numeroOrden,
              detalle: item.detalle || '',
              items: [],
            })
            grupos.push(mapa.get(numeroOrden))
          }

          const grupo = mapa.get(numeroOrden)
          grupo.items.push({ item, index })

          if (!grupo.detalle && item.detalle) grupo.detalle = item.detalle
        })

        resultado[key] = grupos
      })

      return resultado
    },

    resumenPaciente() {
      const paciente = this.formulario.paciente
      if (!paciente) return null

      const persona = paciente.persona || {}
      const identificacion = paciente.identificacion || persona.identificacion || paciente.numeroDocumento || '-'
      const nombres = this.obtenerNombrePaciente(paciente)
      const edad = persona.fechaNacimiento ? this.calcularEdad(persona.fechaNacimiento) : '-'

      return {
        identificacion: identificacion || '-',
        nombres,
        edad,
      }
    },

    resumenPaquete() {
      if (!this.paqueteSeleccionado) return null
      const { nombre, precio } = this.paqueteSeleccionado
      const currencyFilter = this.$options?.filters?.currency
      const precioFormateado = precio != null && currencyFilter
        ? currencyFilter(precio)
        : (precio != null ? precio : '-')

      return {
        nombre: nombre || 'Paquete sin nombre',
        precio: precioFormateado,
      }
    },

    resumenServicios() {
      return this.tiposServicios
        .map(({ key, title }) => {
          const lista = Array.isArray(this.servicios[key]) ? this.servicios[key] : []
          if (!lista.length) return null

          const items = lista
            .map(item => {
              if (key === 'atenciones') return this.formatearResumenAtencion(item)
              return item.descripcion || item.label || item.nombre || 'Sin descripción'
            })

          return {
            key,
            title,
            items,
          }
        })
        .filter(Boolean)
    },

    resumenDocumentos() {
      const entries = [
                { key: 'otro', label: 'Otros documentos' },
      ]

      return entries.map(item => ({
        ...item,
        url: this.obtenerUrlDocumento(item.key),
        disponible: this.tieneDocumentoDisponible(item.key),
      }))
    },
  },

  filters: {
    currency(value) {
      if (!value && value !== 0) return '-'
      return new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(Number(value))
    },
  },

  watch: {
    'formulario.paquete': {
      handler(newValue) {
        if (!newValue) {
          this.paqueteSeleccionado = null
          this.servicios = { imagenes: [], laboratorios: [], procedimientos: [], atenciones: [], terapias: [] }
          sessionStorage.removeItem(STORAGE_KEY)
          return
        }

        this.prepararPaquete(newValue)
      },
      deep: true,
    },
    'modalEdicion.form.atencionSeleccion'(newVal) {
      this.syncAtencionSeleccion(newVal)
    },
    'visorDocumento.visible'(visible) {
      if (!visible) {
        this.visorDocumento.url = null
        this.visorDocumento.rutaCompleta = false
      }
    },
    initialData: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.cargarDesdeAsignacion(newValue)
        } else {
          this.resetFormulario()
        }
      },
    },
  },

  created() {
    this.restaurarDesdeStorage()
    this.fetchModalidades()
  },

  methods: {
    async buscarPaciente(term) {
      const response = await pacientesService.searchPacientesByTermSelect(term)
      return response || []
    },

    async buscarPaquete(term) {
      const response = await paquetesService.searchSelect(term)
      return response || []
    },

    async buscarDoctorPorEspecialidad(term, especialidadId) {
      if (!especialidadId) return []
      const params = {
        search: term,
        especialidad: Number(especialidadId),
        page: 1,
        pageSize: 10,
      }

      try {
        const response = await doctoresService.getAll(params)
        const doctores = response?.doctores || []
        return doctores.map(doctor => ({
          ...doctor,
          value: doctor.id,
          text: `${doctor.idPersona.primerApellido} ${doctor.idPersona.primerNombre}`,
        }))
      } catch (error) {
        console.error('Error buscando doctores por especialidad', error)
        return []
      }
    },

    construirVariablesAsignacion() {
      const idPaciente = this.obtenerIdEntidadObligatorio(
        this.formulario.paciente,
        'paciente',
        ['id', 'value', 'idPaciente', 'pacienteId']
      )
      const idPaquete = this.obtenerIdEntidadObligatorio(
        this.formulario.paquete,
        'paquete preventivo',
        ['id', 'value', 'idPaquete', 'paqueteId']
      )

      return {
        id_paciente: idPaciente,
        id_paquete: idPaquete,
        atenciones_medica: this.construirPayloadAtenciones(),
        imagenes: this.construirPayloadImagenes(),
        procedimientos: this.construirPayloadProcedimientos(),
        laboratorio: this.construirPayloadLaboratorios(),
        terapias: this.construirPayloadTerapias(),
      }
    },

    construirPayloadAtenciones() {
      return this.obtenerListaServicios('atenciones')
        .map(item => {
          const tipoAtencion = this.extraerAtencionId(item)
          if (!tipoAtencion) return null

          const payload = {
            tipo_atencion: tipoAtencion,
            modalidad: this.normalizarModalidad(item.modalidadDescripcion || item.modalidad || item.tipoAtencion),
            fecha: this.normalizarFechaEnvio(item.fechaCita),
            hora: this.normalizarHoraEnvio(item.horaCita),
          }

          const especialidadId = this.extraerEspecialidadId(item)
          if (especialidadId != null) payload.especialidad = especialidadId

          const idDoctor = this.extraerDoctorId(item.doctor)
          if (idDoctor != null) payload.id_doctor = idDoctor

          return payload
        })
        .filter(Boolean)
    },

    construirPayloadImagenes() {
      return this.obtenerListaServicios('imagenes')
        .map(item => {
          const idImagen = this.extraerIdGenerico(item, ['imagenId', 'imagen_id', 'id', 'value', 'codigo'])
          if (!idImagen) return null

          return {
            id_imagen: idImagen,
            numero_orden: item.numeroOrden || null,
            lateralidad: item.lateralidad || null,
            sedacion: this.obtenerValorSedacion(item),
            motivo: item.motivo || item.detalle || '',
            resumen: item.resumenClinico || '',
          }
        })
        .filter(Boolean)
    },

    construirPayloadProcedimientos() {
      return this.obtenerListaServicios('procedimientos')
        .map(item => {
          const idProcedimiento = this.extraerIdGenerico(item, ['procedimientoId', 'procedimiento_id', 'id', 'value', 'codigo'])
          if (!idProcedimiento) return null

          return {
            id_procedimiento: idProcedimiento,
            numero_orden: item.numeroOrden || null,
            lateralidad: item.lateralidad || null,
            sedacion: this.obtenerValorSedacion(item),
            motivo: item.motivo || item.detalle || '',
            resumen: item.resumenClinico || '',
          }
        })
        .filter(Boolean)
    },

    construirPayloadTerapias() {
      return this.obtenerListaServicios('terapias')
        .map(item => {
          const idTerapia = this.extraerIdGenerico(item, ['terapiaId', 'terapia_id', 'id', 'value', 'codigo'])
          if (!idTerapia) return null

          return {
            id_terapia: idTerapia,
            numero_orden: item.numeroOrden || null,
            lateralidad: item.lateralidad || null,
            sedacion: this.obtenerValorSedacion(item),
            motivo: item.motivo || item.detalle || '',
            resumen: item.resumenClinico || '',
          }
        })
        .filter(Boolean)
    },

    construirPayloadLaboratorios() {
      const lista = this.obtenerListaServicios('laboratorios')
      if (!lista.length) return []

      const agrupados = new Map()

      lista.forEach(item => {
        const numeroOrden = item.numeroOrden || this.numeroOrdenLaboratorios || null
        const laboratorioId = this.extraerIdGenerico(item, ['laboratorioId', 'laboratorio_id', 'id', 'value', 'codigo'])
        if (!numeroOrden || !laboratorioId) return

        if (!agrupados.has(numeroOrden)) agrupados.set(numeroOrden, new Set())
        agrupados.get(numeroOrden).add(laboratorioId)
      })

      return Array.from(agrupados.entries()).map(([numero_orden, codigos]) => ({
        numero_orden,
        laboratorios: Array.from(codigos),
      }))
    },

    obtenerListaServicios(tipo) {
      const lista = this.servicios && this.servicios[tipo]
      return Array.isArray(lista) ? lista : []
    },

    extraerDoctorId(doctor) {
      if (!doctor) return null
      if (typeof doctor === 'number') return doctor
      if (typeof doctor === 'string') {
        const numero = this.normalizarEntero(doctor)
        return numero
      }

      const candidato = this.extraerIdGenerico(doctor, ['id', 'value', 'idDoctor', 'doctorId'])
      const numero = this.normalizarEntero(candidato)
      if (numero != null) return numero

      if (doctor.idPersona) {
        const personaId = this.extraerIdGenerico(doctor.idPersona, ['id', 'value'])
        const personaNumero = this.normalizarEntero(personaId)
        if (personaNumero != null) return personaNumero
      }

      if (doctor.persona) {
        const personaId = this.extraerIdGenerico(doctor.persona, ['id'])
        const personaNumero = this.normalizarEntero(personaId)
        if (personaNumero != null) return personaNumero
      }

      return null
    },

    extraerEspecialidadId(servicio) {
      if (!servicio) return null

      const candidato = this.extraerIdGenerico(
        servicio,
        ['especialidadId', 'especialidad_id', 'especialidad']
      )

      const numero = this.normalizarEntero(candidato)
      if (numero != null) return numero

      if (servicio.especialidad && typeof servicio.especialidad === 'object') {
        const interno = this.extraerIdGenerico(servicio.especialidad, ['id', 'value', 'especialidadId'])
        const internoNumero = this.normalizarEntero(interno)
        if (internoNumero != null) return internoNumero
      }

      return null
    },

    extraerAtencionId(item) {
      if (!item) return null

      const atencionSeleccion = item.atencionSeleccion || null
      const candidatoSeleccion = this.extraerIdGenerico(atencionSeleccion, ['value', 'id'])
      const candidatoItem = this.extraerIdGenerico(item, ['atencionId', 'atencion_id', 'id', 'value', 'codigo'])
      const candidato = candidatoSeleccion ?? candidatoItem

      if (candidato == null) return null

      const numero = this.normalizarEntero(candidato)
      return numero != null ? numero : candidato
    },

    extraerIdGenerico(origen, claves = ['id', 'value']) {
      if (origen == null) return null

      if (typeof origen === 'number') return origen

      if (typeof origen === 'string') {
        const texto = origen.trim()
        if (!texto) return null
        const numero = Number(texto)
        return Number.isFinite(numero) ? numero : texto
      }

      if (typeof origen === 'object') {
        for (const clave of claves) {
          if (origen[clave] !== undefined && origen[clave] !== null && origen[clave] !== '') {
            const valor = origen[clave]
            if (typeof valor === 'number') return valor
            if (typeof valor === 'string') {
              const texto = valor.trim()
              if (!texto) continue
              const numero = Number(texto)
              return Number.isFinite(numero) ? numero : texto
            }
            return valor
          }
        }
      }

      return null
    },

    obtenerValorSedacion(item) {
      if (!item) return null
      if (item.sedacion !== undefined && item.sedacion !== null && item.sedacion !== '') return item.sedacion
      if (item.sedacionId !== undefined && item.sedacionId !== null && item.sedacionId !== '') return item.sedacionId
      if (item.requiereSedacion !== undefined && item.requiereSedacion !== null) return item.requiereSedacion ? 1 : 0
      return null
    },

    normalizarModalidad(valor) {
      if (!valor) return null
      const texto = String(valor).trim()
      return texto ? texto.toUpperCase() : null
    },

    normalizarFechaEnvio(valor) {
      if (!valor) return null
      const texto = String(valor).trim()
      if (!texto) return null
      const formatoLatam = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (formatoLatam) {
        const [, dia, mes, anio] = formatoLatam
        return `${anio}-${mes}-${dia}`
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) return texto
      return texto
    },

    normalizarHoraEnvio(valor) {
      if (!valor) return null
      const texto = String(valor).trim()
      if (!texto) return null
      const match = texto.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
      if (match) {
        const [, horas, minutos] = match
        return `${horas.padStart(2, '0')}:${minutos}`
      }
      return texto
    },

    normalizarEntero(valor) {
      if (valor === null || valor === undefined || valor === '') return null
      const numero = Number(valor)
      return Number.isFinite(numero) ? numero : null
    },

    obtenerIdEntidadObligatorio(entidad, etiqueta, claves = ['id', 'value']) {
      const candidato = this.extraerIdGenerico(entidad, claves)
      const numero = this.normalizarEntero(candidato)
      if (numero == null) {
        throw new Error(`No se pudo determinar el ID del ${etiqueta}.`)
      }
      return numero
    },

    prepararPaquete(paqueteRaw) {
      const paquete = {
        id: paqueteRaw?.id,
        token: paqueteRaw?.token,
        codigo: paqueteRaw?.codigo || paqueteRaw?.jsonParsed?.codigo,
        nombre: paqueteRaw?.jsonParsed?.nombre || 'Paquete sin nombre',
        precio: paqueteRaw?.jsonParsed?.precio || paqueteRaw?.jsonParsed?.precios?.valor || null,
        descripcion: paqueteRaw?.jsonParsed?.descripcion || '',
        json: paqueteRaw?.jsonParsed || {},
      }

      this.paqueteSeleccionado = paquete
      this.numeroOrdenLaboratorios = null

      const serviciosPaquete = paquete.json?.servicios || {}

      this.servicios = {
        imagenes: this.formatearServicios(serviciosPaquete?.imagenes, 'imagenes'),
        laboratorios: this.formatearServicios(serviciosPaquete?.laboratorio, 'laboratorios'),
        procedimientos: this.formatearServicios(serviciosPaquete?.procedimientos, 'procedimientos'),
        terapias: this.formatearServicios(serviciosPaquete?.terapias, 'terapias'),
        atenciones: this.formatearServicios(serviciosPaquete?.atencion_medica, 'atenciones'),
      }

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ paquete: paqueteRaw, servicios: this.servicios }))
    },

    formatearServicios(lista = [], tipo) {
      if (!Array.isArray(lista)) return []

      const expandido = []
      const isLaboratorio = tipo === 'laboratorios'
      const numeroOrdenLaboratorio = isLaboratorio ? this.obtenerNumeroOrdenLaboratoriosBase(lista) : null

      lista.forEach((item, idxBase) => {
        const cantidadTotal = Number(item.cantidad) || 1
        const descripcionAtencion = item.especialidad_text || item.descripcion || 'Atención médica'
        const codigoAtencion = item.proc_codigo || item.codigo || 'N/D'

        for (let i = 0; i < cantidadTotal; i += 1) {
          const base = {
            ...item,
            numeroOrden: isLaboratorio
              ? numeroOrdenLaboratorio
              : (item.numeroOrden || this.generarNumeroOrden()),
            detalle: item.detalle || '',
            motivo: item.motivo || '',
            resumenClinico: item.resumenClinico || '',
            lateralidad: item.lateralidad || null,
            requiereSedacion: Boolean(item.requiereSedacion),
            tipo,
            cantidad: 1,
            loteCantidad: cantidadTotal,
            loteIndex: i + 1,
            loteBaseIndex: idxBase,
          }

          if (tipo === 'atenciones') {
            base.descripcion = descripcionAtencion
            base.codigo = codigoAtencion
            base.tipoAtencion = item.tipo || null
            const modalidadBase = item.modalidadDescripcion || item.modalidad || item.tipo || null
            base.modalidadDescripcion = modalidadBase
            base.modalidadId = modalidadBase ? modalidadBase.toUpperCase() : (item.modalidadId || item.modalidad_id || null)
            base.especialidadId = item.especialidad_id || null
            base.especialidadCodigo = item.especialidad_codigo || null
            base.doctor = item.doctor || null
            base.fechaCita = item.fechaCita || null
            base.horaCita = item.horaCita || null
            base.tipoContingencia = item.tipoContingencia || this.defaultTipoContingencia
          }

          if (tipo === 'imagenes') {
            base.codigo = item.codigo || item.label || 'N/D'
          }

          expandido.push(base)
        }
      })

      if (isLaboratorio) this.numeroOrdenLaboratorios = numeroOrdenLaboratorio

      return expandido
    },

    obtenerNumeroOrdenLaboratoriosBase(lista = []) {
      if (this.numeroOrdenLaboratorios) return this.numeroOrdenLaboratorios

      const existente = Array.isArray(lista)
        ? lista.find(item => item && item.numeroOrden)?.numeroOrden
        : null

      const numeroOrden = existente || this.generarNumeroOrden()
      this.numeroOrdenLaboratorios = numeroOrden
      return numeroOrden
    },

    sincronizarNumeroOrdenLaboratorios(numeroOrden) {
      if (!numeroOrden) return

      this.numeroOrdenLaboratorios = numeroOrden
      const lista = this.servicios?.laboratorios
      if (!Array.isArray(lista)) return

      lista.forEach((servicio, index) => {
        if (!servicio || servicio.numeroOrden === numeroOrden) return
        this.$set(lista, index, { ...servicio, numeroOrden })
      })
    },

    restaurarDesdeStorage() {
      const raw = sessionStorage.getItem(STORAGE_KEY)
      if (!raw) return

      try {
        const parsed = JSON.parse(raw)
        if (parsed?.paquete) {
          this.formulario.paquete = parsed.paquete
          this.prepararPaquete(parsed.paquete)
        }
        if (parsed?.servicios) {
          this.servicios = this.normalizarServiciosGuardados(parsed.servicios)
          this.persistirServicios()
        }
      } catch (error) {
        console.error('Error restaurando paquete desde sessionStorage', error)
      }
    },

    handleNext(index, next) {
      const validaciones = [
        this.validatePasoInformacion,
        this.validatePasoServicios,
        this.validatePasoDocumentos,
        this.validatePasoEncuesta,
      ]

      const validator = validaciones[index]
      if (!validator || validator()) next()
    },

    validatePasoInformacion() {
      this.errores.paciente = this.formulario.paciente ? null : 'Selecciona un paciente.'
      this.errores.paquete = this.formulario.paquete ? null : 'Selecciona un paquete preventivo.'
      return !this.errores.paciente && !this.errores.paquete
    },

    validatePasoServicios() {
      if (!this.paqueteSeleccionado) {
        this.$bvToast?.toast('Debes seleccionar un paquete antes de continuar.', { variant: 'warning', solid: true })
        return false
      }

      const hayServicios = Object.values(this.servicios).some(arr => Array.isArray(arr) && arr.length > 0)
      if (!hayServicios) {
        this.$bvToast?.toast('El paquete seleccionado no contiene servicios configurados.', { variant: 'danger', solid: true })
      }

      return hayServicios
    },

    validatePasoDocumentos() {
      return true
    },

    validatePasoEncuesta() {
      if (this.modoEdicion) return true;
      this.errores.encuesta = this.encuestaValida ? null : 'Ingrese un valor entre 1 y 4.'
      if (this.errores.encuesta) {
        this.$bvToast?.toast('Califica la encuesta con un valor entre 1 y 4.', { variant: 'warning', solid: true })
      }
      return !this.errores.encuesta
    },

    onEncuestaInput(value) {
      if (value === null || value === undefined || value === '') {
        this.formulario.encuesta = null
        return
      }

      const parsed = Number(value)
      if (!Number.isFinite(parsed)) {
        this.formulario.encuesta = null
        return
      }

      const normalized = Math.trunc(parsed)
      const bounded = Math.min(4, Math.max(1, normalized))
      this.formulario.encuesta = bounded
    },

    abrirModalEdicion(tipo, indice) {
      const lista = this.servicios[tipo]
      if (!lista || !lista[indice]) return

      const item = lista[indice]

      const comparteOrdenLaboratorio = tipo === 'laboratorios'
      const numeroOrden = comparteOrdenLaboratorio
        ? this.obtenerNumeroOrdenLaboratoriosBase(lista)
        : (item.numeroOrden || this.generarNumeroOrden())

      if (comparteOrdenLaboratorio) {
        this.sincronizarNumeroOrdenLaboratorios(numeroOrden)
      } else if (!item.numeroOrden) {
        this.$set(lista, indice, { ...item, numeroOrden })
      }

      const atencionSeleccion = this.mapAtencionSeleccion(item)
      const modalidadId = item.modalidadDescripcion
        ? item.modalidadDescripcion.toUpperCase()
        : (item.modalidadId || item.tipoAtencionId || null)
      const isPlaceholderAtencion = atencionSeleccion?.cod === 'N/D'

      this.modalEdicion = {
        visible: true,
        tipo,
        indice,
        titulo: `Editar ${this.obtenerTituloTipo(tipo).toLowerCase()}`,
        form: {
          descripcion: item.descripcion || item.label,
          codigo: item.codigo || '',
          numeroOrden,
          detalle: item.detalle || '',
          motivo: item.motivo || '',
          resumenClinico: item.resumenClinico || '',
          lateralidad: item.lateralidad || null,
          requiereSedacion: Boolean(item.requiereSedacion),
          doctor: item.doctor || null,
          fechaCita: item.fechaCita || null,
          horaCita: item.horaCita || null,
          tipoContingencia: item.tipoContingencia || this.defaultTipoContingencia,
          especialidadId: item.especialidadId || null,
          modalidadId,
          modalidadDescripcion: item.modalidadDescripcion || item.tipoAtencion || null,
          atencionSeleccion: isPlaceholderAtencion ? null : atencionSeleccion,
        },
      }

      this.$nextTick(() => {
        this.syncAtencionSeleccion(this.modalEdicion.form?.atencionSeleccion)
        this.onChangeModalidad()
      })
    },

    guardarEdicionServicio(evt) {
      if (evt) evt.preventDefault()

      const { tipo, indice, form } = this.modalEdicion
      if (!tipo || indice === null) return

      const lista = this.servicios[tipo]
      if (!lista || !lista[indice]) return

      if (tipo === 'atenciones') {
        if (!form.doctor) {
          this.$bvToast?.toast('Selecciona un doctor para la atención.', { variant: 'warning', solid: true })
          return
        }
        if (!form.fechaCita) {
          this.$bvToast?.toast('Selecciona la fecha de la cita.', { variant: 'warning', solid: true })
          return
        }
        if (!form.horaCita) {
          this.$bvToast?.toast('Selecciona la hora de la cita.', { variant: 'warning', solid: true })
          return
        }
      }

      if (tipo === 'imagenes') {
        const lateralidadValida = Boolean(form.lateralidad)
        const motivoValido = Boolean(form.motivo && form.motivo.trim())
        const resumenValido = Boolean(form.resumenClinico && form.resumenClinico.trim())

        if (!lateralidadValida) {
          this.$bvToast?.toast('Selecciona la lateralidad del estudio.', { variant: 'warning', solid: true })
          return
        }

        if (!motivoValido) {
          this.$bvToast?.toast('Ingresa el motivo de la solicitud.', { variant: 'warning', solid: true })
          return
        }

        if (!resumenValido) {
          this.$bvToast?.toast('Ingresa el resumen clínico.', { variant: 'warning', solid: true })
          return
        }
      }

      const atencionSeleccion = form.atencionSeleccion || null
      const descripcionSeleccionada = atencionSeleccion?.descripcion || this.extraerDescripcion(atencionSeleccion?.text) || form.descripcion
      const codigoSeleccionado = atencionSeleccion?.cod || this.extraerCodigo(atencionSeleccion?.text) || form.codigo
      const modalidadOption = this.modalidades.find(opt => opt.value === form.modalidadId)
      const modalidadDescripcion = modalidadOption?.text || form.modalidadDescripcion || null

      this.$set(lista, indice, {
        ...lista[indice],
        descripcion: descripcionSeleccionada,
        codigo: codigoSeleccionado,
        numeroOrden: form.numeroOrden,
        detalle: tipo === 'procedimientos' ? null : form.detalle,
        motivo: form.motivo,
        resumenClinico: form.resumenClinico,
        lateralidad: form.lateralidad,
        requiereSedacion: form.requiereSedacion,
        doctor: form.doctor || null,
        fechaCita: form.fechaCita || null,
        horaCita: form.horaCita || null,
        tipoContingencia: form.tipoContingencia || this.defaultTipoContingencia,
        atencionSeleccion,
        atencionId: atencionSeleccion?.value || lista[indice].atencionId || null,
        modalidadId: form.modalidadId || null,
        modalidadDescripcion,
        tipoAtencion: modalidadDescripcion,
      })

      if (tipo === 'laboratorios') {
        this.sincronizarNumeroOrdenLaboratorios(form.numeroOrden)
      }

      this.persistirServicios()
      this.$bvToast?.toast('Información actualizada correctamente.', { variant: 'success', solid: true })
      this.modalEdicion.visible = false
    },

    resetModalEdicion() {
      this.modalEdicion = { visible: false, tipo: null, indice: null, titulo: '', form: null }
    },

    obtenerTituloTipo(tipo) {
      const item = this.tiposServicios.find(s => s.key === tipo)
      return item ? item.title : 'Servicio'
    },

    async fetchModalidades() {
      const defaultModalidades = [
        { value: null, text: 'Seleccione una modalidad' },
        { value: 'PRESENCIAL', text: 'Presencial' },
        { value: 'VIRTUAL', text: 'Virtual' },
      ]

      const query = `
        query {
          allModalidades {
            id
            descripcion
          }
        }
      `

      try {
        const response = await axios.post(rutas.axios + 'api', { query })
        const data = response?.data?.data?.allModalidades || []

        const mapped = data
          .map(item => {
            const desc = (item.descripcion || '').toUpperCase()
            if (desc.includes('VIRTUAL')) return { value: 'VIRTUAL', text: 'Virtual' }
            if (desc.includes('PRESENCIAL')) return { value: 'PRESENCIAL', text: 'Presencial' }
            return null
          })
          .filter(Boolean)

        const unique = mapped.length
          ? [{ value: null, text: 'Seleccione una modalidad' }, ...mapped.filter((opt, index, arr) => arr.findIndex(o => o.value === opt.value) === index)]
          : defaultModalidades

        this.modalidades = unique
      } catch (error) {
        console.error('Error al obtener modalidades de atención', error)
        this.modalidades = defaultModalidades
      }
    },

    async buscarTipoAtencionMedica(term) {
      if (!term || term.length < 3) return []
      const establecimiento = getEstablecimientoLS()
      const establecimientoId = establecimiento?.id || null
      const opciones = await carteraAtencionesService.searchByQueryEstablecimiento(term, establecimientoId)
      if (!opciones) return []

      return opciones.map(opt => ({
        ...opt,
        descripcion: opt.descripcion || this.extraerDescripcion(opt.text),
      }))
    },

    syncAtencionSeleccion(option) {
      if (!this.modalEdicion?.form || !option) return

      const descripcion = option.descripcion || this.extraerDescripcion(option.text) || this.modalEdicion.form.descripcion
      const codigo = option.cod || this.extraerCodigo(option.text) || this.modalEdicion.form.codigo
      this.$set(this.modalEdicion.form, 'descripcion', descripcion)
      this.$set(this.modalEdicion.form, 'codigo', codigo)

      if (option.modalidadId) {
        this.modalEdicion.form.modalidadId = option.modalidadId
        this.modalEdicion.form.modalidadDescripcion = option.modalidad || option.modalidadDescripcion || this.modalEdicion.form.modalidadDescripcion
        this.onChangeModalidad()
      }
    },

    onChangeModalidad() {
      if (!this.modalEdicion?.form) return

      const option = this.modalidades.find(opt => opt.value === this.modalEdicion.form.modalidadId)
      this.modalEdicion.form.modalidadDescripcion = option?.text || this.modalEdicion.form.modalidadDescripcion || null
    },

    extraerCodigo(text) {
      if (!text) return null
      const [codigo] = text.split(' - ')
      return codigo ? codigo.trim() : null
    },

    extraerDescripcion(text) {
      if (!text) return null
      const parts = text.split(' - ')
      parts.shift()
      return parts.join(' - ').trim() || null
    },

    mapAtencionSeleccion(item) {
      if (!item) return null

      if (item.tipo && item.tipo !== 'atenciones') return null

      if (item.atencionSeleccion) return item.atencionSeleccion

      const descripcion = item.descripcion || null
      const codigo = item.codigo || null

      if (!descripcion && !codigo) return null

      return {
        value: item.atencionId || null,
        text: codigo ? `${codigo} - ${descripcion}` : descripcion,
        cod: codigo,
        descripcion,
        modalidadId: item.modalidadId || null,
        modalidad: item.modalidadDescripcion || item.tipoAtencion || null,
      }
    },

    persistirServicios() {
      const current = sessionStorage.getItem(STORAGE_KEY)
      if (!current) return

      try {
        const parsed = JSON.parse(current)
        parsed.servicios = this.servicios
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(parsed))
      } catch (error) {
        console.error('No se pudo actualizar los servicios en sessionStorage', error)
      }
    },

    async submitAsignacion() {
      if (this.isSubmitting) return

      this.errores.encuesta = this.encuestaValida ? null : 'Ingrese un valor entre 1 y 4.'

      const informacionValida = this.validatePasoInformacion()
      const serviciosValidos = this.validatePasoServicios()
      const encuestaValida = this.validatePasoEncuesta()

      if (!informacionValida || !serviciosValidos || !encuestaValida) return

      const confirmado = await this.confirmarFinalizacion()
      if (!confirmado) return

      this.isSubmitting = true

      try {
        const variables = this.construirVariablesAsignacion()
        const respuesta = await asignacionPaquetesService.crearAsignacionPaquete(variables)

        const status = String(respuesta?.status || '').toUpperCase()
        const exito = !status || ['OK', 'SUCCESS', 'TRUE'].includes(status)
        const mensaje = respuesta?.message || 'Asignación registrada correctamente.'

        if (!exito) throw new Error(mensaje || 'No se pudo registrar la asignación.')

        this.$bvToast?.toast(mensaje, {
          title: 'Asignación de paquete',
          variant: 'success',
          solid: true,
        })

        sessionStorage.removeItem(STORAGE_KEY)
        this.$emit('saved', { variables, respuesta })
        this.resetFormulario()
      } catch (error) {
        console.error('Error al asignar paquete', error)
        const message = error?.message || 'Ocurrió un error al guardar la asignación.'
        this.$bvToast?.toast(message, { variant: 'danger', solid: true })
      } finally {
        this.isSubmitting = false
      }
    },

    resetFormulario() {
      this.formulario = {
        paciente: null,
        paquete: null,
        encuesta: null,
      }
      this.errores = {
        paciente: null,
        paquete: null,
        encuesta: null,
      }
      this.servicios = {
        imagenes: [],
        laboratorios: [],
        procedimientos: [],
        atenciones: [],
        terapias: [],
      }
      this.numeroOrdenLaboratorios = null
      this.documentos = {
        otro: null,
      }
      this.documentosPreview = {
        otro: null,
      }
      this.modalEdicion = {
        visible: false,
        tipo: null,
        indice: null,
        titulo: '',
        form: null,
      }

      sessionStorage.removeItem(STORAGE_KEY)
      this.modoEdicion = false
    },

    cargarDesdeAsignacion(data) {
      this.resetFormulario()

      if (!data || typeof data !== 'object') return

      this.modoEdicion = Boolean(data.modoEdicion)
      if (data.paciente) this.formulario.paciente = data.paciente
      if (data.paquete) this.formulario.paquete = data.paquete
      if (data.encuesta) this.formulario.encuesta = data.encuesta

      if (data.serviciosDetalle) {
        this.servicios = this.normalizarServiciosGuardados(data.serviciosDetalle)
      } else if (data.servicios) {
        const serviciosPlaceholder = {}
        Object.keys(this.servicios).forEach(key => {
          if (data.servicios[key]) {
            serviciosPlaceholder[key] = [
              {
                codigo: 'N/D',
                descripcion: key,
                cantidad: 1,
                tipo: key,
              },
            ]
          } else {
            serviciosPlaceholder[key] = []
          }
        })
        this.servicios = this.normalizarServiciosGuardados(serviciosPlaceholder)
      }

      if (data.documentos) {
        this.documentos = { ...this.documentos, ...data.documentos }
      }
    },

    normalizarServiciosGuardados(serviciosGuardados = {}) {
      const resultado = {}

      this.tiposServicios.forEach(({ key }) => {
        const lista = Array.isArray(serviciosGuardados[key]) ? serviciosGuardados[key] : []
        const isLaboratorio = key === 'laboratorios'
        const numeroOrdenLaboratorio = isLaboratorio ? this.obtenerNumeroOrdenLaboratoriosBase(lista) : null

        resultado[key] = lista.map((item, index) => {
          const loteCantidad = Number(item.loteCantidad || item.cantidad) || 1
          const loteIndex = Number(item.loteIndex) || (index % loteCantidad) + 1

          return {
            ...item,
            numeroOrden: isLaboratorio
              ? numeroOrdenLaboratorio
              : (item.numeroOrden || this.generarNumeroOrden()),
            detalle: item.detalle || '',
            motivo: item.motivo || '',
            resumenClinico: item.resumenClinico || '',
            lateralidad: item.lateralidad || null,
            requiereSedacion: Boolean(item.requiereSedacion),
            tipo: item.tipo || key,
            cantidad: Number(item.cantidad) || 1,
            loteCantidad,
            loteIndex,
            loteBaseIndex: item.loteBaseIndex != null ? item.loteBaseIndex : 0,
            tipoContingencia: item.tipoContingencia || this.defaultTipoContingencia,
          }
        })
      })

      return resultado
    },

    generarNumeroOrden() {
      return Math.random().toString(36).substring(2, 8).toUpperCase()
    },

    obtenerNombreDoctor(item) {
      if (!item || !item.doctor) return ''
      const doctor = item.doctor
      if (typeof doctor === 'string') return doctor
      if (doctor.text) return doctor.text
      const persona = doctor.idPersona || doctor.persona
      if (persona && (persona.primerNombre || persona.primerApellido)) {
        return `${persona.primerApellido || ''} ${persona.primerNombre || ''}`.trim()
      }
      return ''
    },

    obtenerNombrePaciente(paciente) {
      if (!paciente) return '-'
      const persona = paciente.persona || {}
      const partes = [
        persona.primerNombre,
        persona.segundoNombre,
        persona.primerApellido,
        persona.segundoApellido,
      ].filter(Boolean)

      if (partes.length) return partes.join(' ')
      return paciente.nombreCompleto || paciente.fullName || paciente.label || '-'
    },

    calcularEdad(fechaNacimiento) {
      if (!fechaNacimiento) return '-'
      const fecha = new Date(fechaNacimiento)
      if (Number.isNaN(fecha.getTime())) return '-'

      const hoy = new Date()
      let edad = hoy.getFullYear() - fecha.getFullYear()
      const mes = hoy.getMonth() - fecha.getMonth()
      if (mes < 0 || (mes === 0 && hoy.getDate() < fecha.getDate())) edad -= 1

      return edad >= 0 ? edad : '-'
    },

    obtenerUrlDocumento(key) {
      if (this.documentosPreview && this.documentosPreview[key]) return this.documentosPreview[key]
      const doc = this.documentos && this.documentos[key]
      if (doc && typeof doc === 'string') return doc
      if (doc && typeof doc === 'object' && doc.url) return doc.url
      return null
    },

    tieneDocumentoDisponible(key) {
      const url = this.obtenerUrlDocumento(key)
      if (url) return true

      const doc = this.documentos && this.documentos[key]
      if (!doc) return false

      if (typeof File !== 'undefined' && doc instanceof File) return true

      return false
    },

    async abrirDocumentoResumen(key) {
      try {
        const url = this.obtenerUrlDocumento(key)
        if (url) {
          this.visorDocumento.url = url
          this.visorDocumento.rutaCompleta = this.debeUsarRutaCompleta(url)
          this.visorDocumento.visible = true
          return
        }

        const doc = this.documentos && this.documentos[key]
        if (typeof File !== 'undefined' && doc instanceof File) {
          const dataUrl = await this.leerArchivoComoDataUrl(doc)
          this.visorDocumento.url = dataUrl
          this.visorDocumento.rutaCompleta = true
          this.visorDocumento.visible = true
          return
        }

        this.$bvToast?.toast('No se encontró un archivo para visualizar.', {
          variant: 'info',
          solid: true,
        })
      } catch (error) {
        console.error('Error al abrir documento del resumen', error)
        this.$bvToast?.toast('No se pudo mostrar el documento seleccionado.', {
          variant: 'danger',
          solid: true,
        })
      }
    },

    onVerDocumentoResumen(documento) {
      if (!documento || !documento.key) return
      this.abrirDocumentoResumen(documento.key)
    },

    leerArchivoComoDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => resolve(event.target?.result || null)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    debeUsarRutaCompleta(url) {
      if (!url || typeof url !== 'string') return false
      return /^(data:|https?:|blob:)/i.test(url.trim())
    },

    formatearResumenAtencion(item = {}) {
      const especialidadRaw = item.especialidad_text || item.especialidadDescripcion || item.descripcion || 'Sin especialidad'
      const especialidad = typeof especialidadRaw === 'string' ? especialidadRaw.toUpperCase() : especialidadRaw
      const doctor = this.obtenerNombreDoctor(item) || 'Sin doctor asignado'
      const modalidad = item.modalidadDescripcion || item.tipoAtencion || 'Sin modalidad'
      const fecha = item.fechaCita ? this.formatearFechaCita(item.fechaCita) : 'Sin fecha'
      const hora = item.horaCita ? this.formatearHoraCita(item.horaCita) : 'Sin hora'

      return `Especialidad: ${especialidad} | Doctor asignado: ${doctor} | Modalidad: ${modalidad} | Fecha: ${fecha} | Hora: ${hora}`
    },

    formatearFechaCita(valor) {
      if (!valor) return '-'
      const str = String(valor).trim()
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [anio, mes, dia] = str.split('-')
        return `${dia}/${mes}/${anio}`
      }
      return str
    },

    formatearHoraCita(valor) {
      if (!valor) return '-'
      const str = String(valor).trim()
      const match = str.match(/^(\d{2}):(\d{2})(?::\d{2})?$/)
      if (match) return `${match[1]}:${match[2]}`
      return str
    },

    async confirmarFinalizacion() {
      if (this.$bvModal?.msgBoxConfirm) {
        try {
          return await this.$bvModal.msgBoxConfirm('¿Deseas finalizar y guardar la asignación?', {
            title: 'Confirmar asignación',
            size: 'sm',
            okVariant: 'primary',
            okTitle: 'Sí, guardar',
            cancelTitle: 'Cancelar',
            centered: true,
          })
        } catch (error) {
          console.error('No se pudo mostrar la confirmación de finalización', error)
          return false
        }
      }

      return window.confirm('¿Deseas finalizar y guardar la asignación?')
    },
  },
}
</script>

<style lang="scss" scoped>
.asignacion-paquetes {
  .wizard-button {
    min-width: 130px;
  }

  .servicio-card {
    background-color: #f8fcff;
    border-left: 4px solid #0080c9;
  }

  .lista-laboratorios {
    list-style: none;
    padding-left: 0;
  }

  .lista-laboratorios li + li {
    margin-top: 0.5rem;
  }

  .encuesta-input {
    max-width: 120px;
    font-size: 1.5rem;
    text-align: center;
  }

  .resumen-final {
    background-color: #fdfefe;
  }

  .section-title {
    font-size: 1.1rem;
    color: #0080c9;
  }

  .resumen-list {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
  }

  .resumen-list li + li {
    margin-top: 0.25rem;
  }
}
</style>
